import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createGallery } from "../../services/galleryService";
import { getAllPackages } from "../../services/paymentService";
import Button from "./Button";
import Input from "./Input";
import Modal from "./Modal";
import Switch from "./Switch";
import { color } from "../../styles/theme";
import { toast } from "react-toastify";
import ARoomAlbumPolicyFields, { AROOM_ALBUM_VISIBILITY } from "./ARoomAlbumPolicyFields";

const CreateGalleryModal = ({ eventId, isARoom = false, isPrivateARoom = false, onClose, onCreated }) => {
  const { t } = useTranslation("common");
  const [form, setForm] = useState({
    name: "",
    albumName: "",
    IsPremium: false,
    IsWatermark: false,
    IsWatermarkAlbum: false,
    MakeAlbumsPremium: false,
    PackageId: "",
    ContainsMinors: false,
    ARoomVisibility: isPrivateARoom ? AROOM_ALBUM_VISIBILITY.VerifiedMembers : AROOM_ALBUM_VISIBILITY.Public,
    ConsentConfirmed: false,
  });
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesLoaded, setPackagesLoaded] = useState(false);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  useEffect(() => {
    if (!form.IsPremium || packagesLoaded || packagesLoading) return;

    const loadPackages = async () => {
      setPackagesLoading(true);
      try {
        const res = await getAllPackages();
        const raw = Array.isArray(res.data?.Result)
          ? res.data.Result
          : Array.isArray(res.data)
            ? res.data
            : [];
        setPackages(raw.filter((pkg) => pkg?.IsActive !== false));
        setPackagesLoaded(true);
      } catch (err) {
        toast.error(err.response?.data?.Message || t("createGalleryModal.loadPackagesError"));
      } finally {
        setPackagesLoading(false);
      }
    };

    loadPackages();
  }, [form.IsPremium, packagesLoaded, packagesLoading, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.warning(t("createGalleryModal.nameRequired")); return; }
    if (!form.albumName.trim()) { toast.warning(t("createGalleryModal.albumNameRequired")); return; }
    if (form.IsPremium && !form.PackageId) {
      toast.warning(t("createGalleryModal.packageRequired"));
      return;
    }

    setLoading(true);
    try {
      const res = await createGallery({
        eventId: Number(eventId),
        name: form.name.trim(),
        isPremium: form.IsPremium,
        packageId: form.IsPremium ? Number(form.PackageId) : null,
        MakeAlbumsPremium: form.MakeAlbumsPremium,
        IsWatermark: form.IsWatermark,
        AlbumName: form.albumName.trim(),
        IsWatermarkAlbum: form.IsWatermarkAlbum,
        ...(isARoom ? {
          DefaultAlbumContainsMinors: form.ContainsMinors,
          DefaultAlbumARoomVisibility: form.ContainsMinors ? AROOM_ALBUM_VISIBILITY.IndividuallyApproved : form.ARoomVisibility,
          DefaultAlbumConsentConfirmed: form.ConsentConfirmed,
        } : {}),
      });
      toast.success(res.data?.Message || t("createGalleryModal.createSuccess"));
      onCreated(res.data?.Result);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("createGalleryModal.createError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t("createGalleryModal.title")}
      onClose={onClose}
      busy={loading}
      maxWidth={460}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button" disabled={loading}>{t("actions.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={loading}>{loading ? t("createGalleryModal.creating") : t("createGalleryModal.create")}</Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <Input label={t("createGalleryModal.nameLabel")} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("createGalleryModal.namePlaceholder")} required />
        <Input label={t("createGalleryModal.albumNameLabel")} value={form.albumName} onChange={(e) => set("albumName", e.target.value)} placeholder={t("createGalleryModal.albumNamePlaceholder")} required />

        <div style={styles.switchGroup}>
          <SwitchRow label={t("createGalleryModal.watermarkGalleryLabel")} checked={form.IsWatermark} onChange={(v) => set("IsWatermark", v)} />
          <SwitchRow label={t("createGalleryModal.watermarkAlbumLabel")} checked={form.IsWatermarkAlbum} onChange={(v) => set("IsWatermarkAlbum", v)} />
          <SwitchRow
            label={t("createGalleryModal.premiumLabel")}
            checked={form.IsPremium}
            onChange={(v) => { set("IsPremium", v); if (!v) set("PackageId", ""); }}
            last={!form.IsPremium}
          />
          {form.IsPremium && (
            <div style={styles.selectWrap}>
              <label style={styles.selectLabel}>{t("createGalleryModal.packagePriceLabel")}</label>
              <select value={form.PackageId} onChange={(e) => set("PackageId", e.target.value)} required={form.IsPremium} className="input">
                <option value="">{t("createGalleryModal.packagePricePlaceholder")}</option>
                {packages.map((pkg) => (
                  <option key={pkg.Id} value={pkg.Id}>
                    {Number(pkg.DefaultPrice || 0).toFixed(2)} {pkg.Currency}
                  </option>
                ))}
              </select>
              {packagesLoading && <div style={styles.selectHint}>{t("createGalleryModal.loadingPackagePrices")}</div>}
            </div>
          )}
        </div>
        {isARoom && <ARoomAlbumPolicyFields value={form} onChange={setForm} isPrivateARoom={isPrivateARoom} />}
      </form>
    </Modal>
  );
};

const SwitchRow = ({ label, checked, onChange, last = false }) => (
  <div style={{ ...styles.switchRow, ...(last ? { borderBottom: "none" } : {}) }}>
    <span style={styles.switchLabel}>{label}</span>
    <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </div>
);

const styles = {
  switchGroup: { display: "flex", flexDirection: "column", marginBottom: 4 },
  switchRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 2px", borderBottom: `1px solid ${color.gray100}` },
  switchLabel: { fontSize: 13.5, color: color.gray800, fontWeight: 500 },
  selectWrap: { padding: "10px 2px 2px" },
  selectLabel: { display: "block", marginBottom: 6, fontWeight: 600, fontSize: 13, color: color.gray700 },
  selectHint: { marginTop: 6, fontSize: 12, color: color.textMuted },
};

export default CreateGalleryModal;
